import {
	EarlyAccessStatusEnum,
	useDeleteManyOrganizationMutation,
	useDeleteOrganizationMutation,
	useGetAllOrganizationQuery,
	useGetOrganizationStatsQuery,
	useListEarlyAccessRequestsQuery,
	useRejectEarlyAccessRequestMutation,
} from '@codezo/graphql';
import { ListFooter, PagingModeToggle, usePagedList, type RowEstimate } from '@codezo/lists';
import { toast } from 'sonner';
import { AddClientModal } from './AddClientModal';
import {
	ApproveEarlyAccessDialog,
	type EarlyAccessRequestSummary,
} from './ApproveEarlyAccessDialog';
import { Check, Copy, MoreHorizontal, Plus, Trash2, UserPlus, Users, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetPageMeta } from '../../hooks/useSetPageMeta';
import { useListParams } from '../../hooks/useListParams';
import { useQueryParam } from '../../hooks/useQueryParam';
import { SortableTableHead } from '../../components/ui/sortable-table-head';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '../../components/ui/table';
import { Tabs } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { SelectionBar } from '../../components/ui/selection-bar';
import { SearchInput } from '../../components/ui/search-input';
import { InitialsAvatar } from '../../components/ui/initials-avatar';
import { ViewToggle } from '../../components/ui/view-toggle';
import { EntityCard } from '../../components/ui/entity-card';
import { EmptyState } from '../../components/ui/empty-state';
import { CardSkeletons, TableSkeleton } from '../../components/ui/list-skeletons';
import { formatDate } from '../../lib/utils';

/**
 * One row of clients as laid out at the current viewport, for sizing scroll-mode batches.
 * Mirrors the card grid below (1/2/3 columns at sm/lg, 14px gap) of EntityCards with two
 * metrics: 16px padding, 44px avatar header, 14px spacing and a ~51px metrics strip. A table
 * row is the 32px logo plus cell padding and border.
 */
function clientRows(view: string): RowEstimate {
	if (view !== 'card') return { rowHeight: 53 };
	const vw = window.innerWidth;
	const columns = vw >= 1024 ? 3 : vw >= 640 ? 2 : 1;
	return { columns, rowHeight: 142 + 14 };
}

/* ════════════════════════ CLIENTS PAGE ════════════════════════ */

export function ClientsPage() {
	const navigate = useNavigate();
	useSetPageMeta('Clients', [{ label: 'Clients' }]);
	const {
		search,
		setSearch,
		debouncedSearch,
		page,
		setPage,
		view,
		setView,
		sortField,
		sortDirection,
		sortDir,
		toggleSort,
		limit,
		mode,
		setMode,
		request,
		loadMore,
		reload,
	} = useListParams({
		defaultSort: 'name',
		defaultDir: 'asc',
		defaultView: 'list',
		scrollRows: clientRows,
	});
	const [tab, setTab] = useQueryParam('tab', 'all');
	const [addModalOpen, setAddModalOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

	const { data, loading, refetch } = useGetAllOrganizationQuery({
		variables: {
			input: {
				pagination: request,
				order: { field: sortField, direction: sortDirection },
				search: debouncedSearch || undefined,
			},
		},
		fetchPolicy: 'no-cache',
	});

	const list = usePagedList(
		{ mode, request, limit, setPage, loadMore },
		data?.getAllOrganization,
		loading
	);
	const organizations = list.items;

	// Dataset-wide KPI stats (independent of the active tab/search/page).
	const { data: statsData, refetch: refetchStats } = useGetOrganizationStatsQuery({
		fetchPolicy: 'no-cache',
	});
	const stats = statsData?.getOrganizationStats;

	const refresh = () => {
		reload(refetch);
		refetchStats();
	};

	/* ── Selection state ── */
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

	const orgIds = useMemo(() => organizations.map((o) => o.id), [organizations]);
	const allSelected = orgIds.length > 0 && orgIds.every((id) => selectedIds.has(id));
	const someSelected = orgIds.some((id) => selectedIds.has(id));

	const toggleOne = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const toggleAll = () => {
		if (allSelected) {
			setSelectedIds(new Set());
		} else {
			setSelectedIds(new Set(orgIds));
		}
	};

	// Clear selection when the rows on screen are replaced (page/search change). Scrolling
	// keeps earlier rows on screen, so in scroll mode loading more does not clear it.
	const shownPage = mode === 'pages' ? page : 0;
	useEffect(() => {
		setSelectedIds(new Set());
	}, [shownPage, mode, debouncedSearch]);

	/* ── Single delete ── */
	const [deleteOrganization, { loading: deleting }] = useDeleteOrganizationMutation();
	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			await deleteOrganization({ variables: { input: { id: deleteTarget.id } } });
			toast.success('Client deleted');
			setDeleteTarget(null);
			refresh();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete client');
		}
	};

	/* ── Bulk delete ── */
	const [deleteManyOrganization, { loading: bulkDeleting }] = useDeleteManyOrganizationMutation();
	const handleBulkDelete = async () => {
		try {
			await deleteManyOrganization({
				variables: { input: { ids: Array.from(selectedIds) } },
			});
			toast.success(`${selectedIds.size} client${selectedIds.size > 1 ? 's' : ''} deleted`);
			setSelectedIds(new Set());
			setBulkDeleteOpen(false);
			refresh();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete clients');
		}
	};

	const total = list.pageInfo?.totalCount ?? 0;

	/* ── Early access ── */
	const {
		data: earlyAccessData,
		loading: earlyAccessLoading,
		refetch: refetchEarlyAccess,
	} = useListEarlyAccessRequestsQuery({
		variables: { status: EarlyAccessStatusEnum.Pending },
		fetchPolicy: 'no-cache',
	});
	const earlyAccessRequests = earlyAccessData?.listEarlyAccessRequests ?? [];

	const [rejectRequest, { loading: rejecting }] = useRejectEarlyAccessRequestMutation();
	const [inviteUrl, setInviteUrl] = useState<string | null>(null);
	const [approveTarget, setApproveTarget] = useState<EarlyAccessRequestSummary | null>(null);

	const handleApproved = (inviteToken: string) => {
		setInviteUrl(`${import.meta.env.VITE_PUBLIC_APP_URL}/signup?inviteToken=${inviteToken}`);
		setApproveTarget(null);
		refetchEarlyAccess();
	};

	const handleReject = async (requestId: string) => {
		try {
			const { data } = await rejectRequest({ variables: { requestId } });
			if (data?.rejectEarlyAccessRequest.success) {
				toast.success('Request rejected');
				refetchEarlyAccess();
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to reject request');
		}
	};

	const copyInviteUrl = () => {
		if (inviteUrl) {
			navigator.clipboard.writeText(inviteUrl);
			toast.success('Signup URL copied to clipboard');
			setInviteUrl(null);
		}
	};

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			{/* ── Header ── */}
			<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
				<div>
					<h1 className="mb-2 text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
						Clients
					</h1>
					<p className="text-base text-text-3">
						{total} organizations across your account.
					</p>
				</div>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={() => setAddModalOpen(true)}>
						<Plus size={14} data-icon="inline-start" />
						Add client
					</Button>
				</div>
			</div>

			{/* ── KPI strip ── */}
			<div className="mb-7 grid grid-cols-2 divide-x divide-y divide-line-1 overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-sunken lg:grid-cols-4">
				{[
					{
						label: 'Total clients',
						value: stats?.totalClients ?? 0,
						sub: 'All organizations',
					},
					{ label: 'Active', value: stats?.active ?? 0, sub: 'With an active user' },
					{
						label: 'Industries',
						value: stats?.industries ?? 0,
						sub: 'Across all accounts',
					},
					{ label: 'Recent', value: stats?.recent ?? 0, sub: 'Last 30 days' },
				].map((k) => (
					<div key={k.label} className="px-5 py-4">
						<div className="mb-1.5 text-2xs font-medium uppercase tracking-[0.08em] text-text-4">
							{k.label}
						</div>
						<div className="mb-1 font-mono text-2xl font-medium text-text-1">
							{k.value}
						</div>
						<div className="text-xs text-text-3">{k.sub}</div>
					</div>
				))}
			</div>

			{/* ── Tabs + filters row ── */}
			<div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
				<Tabs
					value={tab}
					onChange={(v) => {
						setTab(v);
						setPage(1);
					}}
					items={[
						{ value: 'all', label: 'All clients', count: total },
						{
							value: 'early-access',
							label: 'Early access',
							count: earlyAccessRequests.length || undefined,
						},
					]}
				/>
				{tab === 'all' && (
					<div className="flex items-center gap-2">
						<SearchInput
							value={search}
							onChange={setSearch}
							placeholder="Search clients…"
							className="flex-1 lg:flex-none lg:w-[240px]"
						/>
						<ViewToggle value={view} onChange={setView} />
						<PagingModeToggle value={mode} onChange={setMode} />
					</div>
				)}
			</div>

			{/* ── All clients tab ── */}
			{tab === 'all' && (
				<>
					{list.loading ? (
						view === 'card' ? (
							<CardSkeletons />
						) : (
							<TableSkeleton />
						)
					) : organizations.length === 0 ? (
						<EmptyState
							icon={Users}
							title="No clients found"
							description={
								search
									? 'Try a different search term.'
									: 'No organizations have been created yet.'
							}
						/>
					) : view === 'card' ? (
						<div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
							{organizations.map((org) => (
								<EntityCard
									key={org.id}
									name={org.name}
									avatar={org.logo}
									maxInitials={3}
									subtitle={org.organizationIndustry?.name || 'No industry'}
									metrics={[
										{
											label: 'Website',
											value: org.websiteUrl
												? org.websiteUrl
														.replace(/^https?:\/\//, '')
														.replace(/\/$/, '')
												: '—',
										},
										{
											label: 'Created',
											value: org.createdAt ? formatDate(org.createdAt) : '—',
										},
									]}
									onClick={() => navigate(`/clients/${org.id}`)}
								/>
							))}
						</div>
					) : (
						<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
							<div className="overflow-x-auto">
								<Table className="min-w-[600px]">
									<TableHeader>
										<TableRow className="border-line-1 hover:bg-transparent">
											<TableHead className="w-10">
												<Checkbox
													checked={allSelected}
													indeterminate={someSelected && !allSelected}
													onCheckedChange={toggleAll}
												/>
											</TableHead>
											<SortableTableHead
												sortKey="name"
												activeKey={sortField}
												direction={sortDir}
												onSort={toggleSort}
												className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
											>
												Client
											</SortableTableHead>
											<SortableTableHead
												sortKey="organizationIndustryId"
												activeKey={sortField}
												direction={sortDir}
												onSort={toggleSort}
												className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
											>
												Industry
											</SortableTableHead>
											<SortableTableHead
												sortKey="websiteUrl"
												activeKey={sortField}
												direction={sortDir}
												onSort={toggleSort}
												className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
											>
												Website
											</SortableTableHead>
											<SortableTableHead
												sortKey="createdAt"
												activeKey={sortField}
												direction={sortDir}
												onSort={toggleSort}
												className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
											>
												Created
											</SortableTableHead>
											<TableHead className="w-10" />
										</TableRow>
									</TableHeader>
									<TableBody>
										{organizations.map((org) => (
											<TableRow
												key={org.id}
												data-state={
													selectedIds.has(org.id) ? 'selected' : undefined
												}
												className="cursor-pointer border-line-1 transition-colors duration-[180ms] hover:bg-surface-hover last:border-b-0"
												onClick={() => navigate(`/clients/${org.id}`)}
											>
												<TableCell>
													<div onClick={(e) => e.stopPropagation()}>
														<Checkbox
															checked={selectedIds.has(org.id)}
															onCheckedChange={() =>
																toggleOne(org.id)
															}
														/>
													</div>
												</TableCell>
												<TableCell>
													<div className="flex items-center gap-3">
														{org.logo ? (
															<img
																src={org.logo}
																alt={org.name}
																className="h-8 w-8 shrink-0 rounded-[var(--r-2)] border border-line-1 object-cover"
															/>
														) : (
															<InitialsAvatar
																name={org.name}
																size={32}
																shape="rounded"
																maxInitials={3}
															/>
														)}
														<span className="text-base font-medium text-text-1">
															{org.name}
														</span>
													</div>
												</TableCell>
												<TableCell className="text-base text-text-2">
													{org.organizationIndustry?.name || '—'}
												</TableCell>
												<TableCell className="text-base text-text-2">
													{org.websiteUrl ? (
														<span className="truncate">
															{org.websiteUrl.replace(
																/^https?:\/\//,
																''
															)}
														</span>
													) : (
														'—'
													)}
												</TableCell>
												<TableCell className="text-base text-text-3">
													{org.createdAt
														? formatDate(org.createdAt)
														: '—'}
												</TableCell>
												<TableCell>
													<div className="flex justify-end">
														<DropdownMenu>
															<DropdownMenuTrigger
																onClick={(e) => e.stopPropagation()}
																className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
															>
																<MoreHorizontal size={14} />
															</DropdownMenuTrigger>
															<DropdownMenuContent align="end">
																{/* No Edit item: it navigated to
																    the client page, which is what
																    clicking the row already does. */}
																<DropdownMenuItem
																	variant="destructive"
																	onClick={(e) => {
																		e.stopPropagation();
																		setDeleteTarget({
																			id: org.id,
																			name: org.name,
																		});
																	}}
																>
																	<Trash2 size={14} /> Delete
																</DropdownMenuItem>
															</DropdownMenuContent>
														</DropdownMenu>
													</div>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						</Card>
					)}

					<SelectionBar
						count={selectedIds.size}
						actions={[
							{
								label: 'Delete',
								icon: Trash2,
								onClick: () => setBulkDeleteOpen(true),
							},
						]}
						onClear={() => setSelectedIds(new Set())}
					/>

					<ListFooter list={list} />
				</>
			)}

			{/* ── Early access tab ── */}
			{tab === 'early-access' && (
				<>
					{earlyAccessLoading ? (
						<TableSkeleton />
					) : earlyAccessRequests.length === 0 ? (
						<Card className="border-line-1 bg-surface py-16">
							<div className="flex flex-col items-center gap-3 text-center">
								<div className="flex h-11 w-11 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
									<UserPlus size={20} />
								</div>
								<div className="text-base text-text-2">No pending requests</div>
								<div className="text-sm text-text-4">
									Early access requests will appear here when users sign up.
								</div>
							</div>
						</Card>
					) : (
						<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
							<div className="overflow-x-auto">
								<Table className="min-w-[600px]">
									<TableHeader>
										<TableRow className="border-line-1 hover:bg-transparent">
											<TableHead className="text-xs font-medium uppercase tracking-[0.06em] text-text-4">
												Client
											</TableHead>
											<TableHead className="text-xs font-medium uppercase tracking-[0.06em] text-text-4">
												Company
											</TableHead>
											<TableHead className="text-xs font-medium uppercase tracking-[0.06em] text-text-4">
												Email
											</TableHead>
											<TableHead className="text-xs font-medium uppercase tracking-[0.06em] text-text-4">
												Requested
											</TableHead>
											<TableHead className="w-[140px]" />
										</TableRow>
									</TableHeader>
									<TableBody>
										{earlyAccessRequests.map((req) => (
											<TableRow
												key={req.id}
												className="border-line-1 last:border-b-0"
											>
												<TableCell>
													<span className="text-base font-medium text-text-1">
														{req.firstName} {req.lastName}
													</span>
												</TableCell>
												<TableCell className="text-base text-text-2">
													{req.companyName || '—'}
												</TableCell>
												<TableCell className="text-base text-text-2">
													{req.email}
												</TableCell>
												<TableCell className="text-base text-text-3">
													{formatDate(req.createdAt)}
												</TableCell>
												<TableCell>
													<div className="flex justify-end gap-1.5">
														<Button
															size="sm"
															onClick={() =>
																setApproveTarget({
																	id: req.id,
																	firstName: req.firstName,
																	lastName: req.lastName,
																	email: req.email,
																	companyName: req.companyName,
																})
															}
															disabled={rejecting}
														>
															<Check
																size={14}
																data-icon="inline-start"
															/>
															Approve
														</Button>
														<Button
															variant="outline"
															size="sm"
															onClick={() => handleReject(req.id)}
															disabled={rejecting}
														>
															<X size={14} data-icon="inline-start" />
															Reject
														</Button>
													</div>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						</Card>
					)}

					{/* Invite URL toast bar */}
					{inviteUrl && (
						<div className="sticky bottom-6 z-20 mt-4 flex items-center justify-between rounded-[var(--r-3)] border border-line-1 bg-surface px-4 py-2.5 shadow-lg">
							<div className="min-w-0 flex-1">
								<div className="mb-0.5 text-xs font-medium text-text-3">
									Invite signup URL
								</div>
								<div className="truncate font-mono text-sm text-text-2">
									{inviteUrl}
								</div>
							</div>
							<div className="ml-3 flex items-center gap-2">
								<Button size="sm" onClick={copyInviteUrl}>
									<Copy size={14} data-icon="inline-start" />
									Copy URL
								</Button>
								<Button
									variant="outline"
									size="sm"
									onClick={() => setInviteUrl(null)}
								>
									Dismiss
								</Button>
							</div>
						</div>
					)}
				</>
			)}

			<ApproveEarlyAccessDialog
				request={approveTarget}
				onOpenChange={(open) => {
					if (!open) setApproveTarget(null);
				}}
				onApproved={handleApproved}
			/>

			<AddClientModal
				open={addModalOpen}
				onOpenChange={setAddModalOpen}
				onSuccess={refresh}
			/>

			<ConfirmDialog
				open={!!deleteTarget}
				onOpenChange={(open) => {
					if (!open) setDeleteTarget(null);
				}}
				title="Delete client"
				description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
				onConfirm={handleDelete}
				loading={deleting}
			/>

			<ConfirmDialog
				open={bulkDeleteOpen}
				onOpenChange={setBulkDeleteOpen}
				title="Delete clients"
				description={`Are you sure you want to delete ${selectedIds.size} client${selectedIds.size > 1 ? 's' : ''}? This action cannot be undone.`}
				onConfirm={handleBulkDelete}
				loading={bulkDeleting}
			/>
		</div>
	);
}
